import type { User } from '@/types/user';

export interface ProfileReadinessItem {
  id: string;
  label: string;
  complete: boolean;
  required: boolean;
  guidance: string;
}

export interface PublicProfileCompanyCompliance {
  company_name?: string | null;
  legal_name?: string | null;
  company_nmls_id?: string | null;
  state_licenses?: Array<{ state?: string | null; license_number?: string | null }> | null;
  headquarters_address?: {
    street?: string | null;
    suite?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
  } | null;
  main_phone?: string | null;
  main_email?: string | null;
  website?: string | null;
  logo_url?: string | null;
  equal_housing_lender?: boolean | null;
  default_disclaimers?: string[] | null;
}

export interface MarketingAuthorizationReadiness {
  gateEnabled: boolean;
  governedByRecruiting: boolean;
  authorized: boolean;
}

function isHttpsUrl(value: unknown): boolean {
  if (typeof value !== 'string' || !value.trim()) {
    return false;
  }
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

function isEmail(value: unknown): boolean {
  return typeof value === 'string'
    && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.trim());
}

function hasText(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

export function isCompanyComplianceReady(company?: PublicProfileCompanyCompliance | null): boolean {
  if (!company) {
    return false;
  }

  const address = company.headquarters_address;
  const licenses = Array.isArray(company.state_licenses) ? company.state_licenses : [];
  const disclaimers = Array.isArray(company.default_disclaimers) ? company.default_disclaimers : [];
  const hasStateLicense = licenses.some(
    (license) => hasText(license.state) && hasText(license.license_number)
  );

  return hasText(company.company_name)
    && hasText(company.legal_name)
    && hasText(company.company_nmls_id)
    && hasText(company.main_phone)
    && isEmail(company.main_email)
    && isHttpsUrl(company.website)
    && company.equal_housing_lender === true
    && disclaimers.some(hasText)
    && hasText(address?.street)
    && hasText(address?.city)
    && hasText(address?.state)
    && hasText(address?.zip)
    && hasStateLicense;
}

export function getProfileReadiness(
  profile: Partial<User>,
  company?: PublicProfileCompanyCompliance | null,
  marketingAuthorization?: MarketingAuthorizationReadiness | null
): ProfileReadinessItem[] {
  const items: ProfileReadinessItem[] = [
    {
      id: 'identity',
      label: 'Identity',
      complete: Boolean(profile.firstName?.trim() && profile.lastName?.trim() && profile.title?.trim()),
      required: true,
      guidance: 'Add first name, last name, and title.'
    },
    {
      id: 'email',
      label: 'Lead notification email',
      complete: isEmail(profile.email),
      required: true,
      guidance: 'Add a valid email address so public profile leads reach this loan officer.'
    },
    {
      id: 'licensing',
      label: 'Licensing',
      complete: Boolean(profile.nmlsNumber?.trim() && (profile.licensedStates?.length || 0) > 0),
      required: true,
      guidance: 'Add an NMLS ID and at least one licensed state.'
    },
    {
      id: 'photo',
      label: 'Profile photo',
      complete: isHttpsUrl(profile.profilePhotoFacial) || isHttpsUrl(profile.profilePhotoBody),
      required: true,
      guidance: 'Upload a headshot or full-body profile photo.'
    },
    {
      id: 'template',
      label: 'Template',
      complete: Boolean(profile.websiteTemplate && profile.websiteTemplate !== 'none'),
      required: true,
      guidance: 'Select one of the three public profile templates.'
    },
    {
      id: 'slug',
      label: 'Public URL',
      complete: Boolean(profile.websiteSlug?.trim()),
      required: true,
      guidance: 'Reserve a public URL. Reserving it does not publish the profile.'
    },
    {
      id: 'application',
      label: 'Personal application link',
      complete: isHttpsUrl(profile.applicationLink),
      required: true,
      guidance: 'Generate the loan officer’s personal Encompass application link.'
    },
    {
      id: 'company-compliance',
      label: 'Company compliance',
      complete: isCompanyComplianceReady(company),
      required: true,
      guidance: 'Complete the legal company name, NMLS, licenses, headquarters, contact details, website, Equal Housing status, and public disclaimer in Company Settings.'
    },
    {
      id: 'bio',
      label: 'Biography',
      complete: Boolean(profile.bio?.trim()),
      required: false,
      guidance: 'Optional. Missing biographies are hidden on the public profile.'
    },
    {
      id: 'programs',
      label: 'Loan programs',
      complete: (profile.mortgageSpecialties?.length || 0) > 0,
      required: false,
      guidance: 'Optional. Add the mortgage programs this officer actually works with.'
    },
    {
      id: 'experience',
      label: 'Industry start year',
      complete: Boolean(profile.mortgageIndustryStartYear),
      required: false,
      guidance: 'Optional. Enter only a staff-confirmed year; do not infer it from NMLS.'
    }
  ];

  if (marketingAuthorization?.gateEnabled && marketingAuthorization.governedByRecruiting) {
    items.splice(8, 0, {
      id: 'nmls_authorization',
      label: 'NMLS authorization',
      complete: marketingAuthorization.authorized,
      required: true,
      guidance: 'NMLS must record authorization to conduct business under LendWise before this profile can be published.'
    });
  }

  return items;
}

export function isProfileReadyToPublish(
  profile: Partial<User>,
  company?: PublicProfileCompanyCompliance | null,
  marketingAuthorization?: MarketingAuthorizationReadiness | null
): boolean {
  return getProfileReadiness(profile, company, marketingAuthorization)
    .filter((item) => item.required)
    .every((item) => item.complete);
}
